var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { useEffect, useMemo } from "react";
import { usePublicAgenda } from "./useAgendaData";
import { endOfDay, startOfDay } from "../../date.util";
import { CommonThemeColors } from "../../../components/theme";
function parseLocaleString(raw) {
    if (!raw || !raw.trim())
        return {};
    var cleaned = raw
        .replace(/^\uFEFF/, "") // strip BOM
        .replace(/,\s*([}\]])/g, "$1"); // strip trailing commas
    try {
        var obj = JSON.parse(cleaned);
        return typeof obj === "object" && obj ? obj : {};
    }
    catch (_a) {
        return {};
    }
}
export var useAgendaEvents = function (agendaId, selected) {
    var _a = usePublicAgenda(agendaId, startOfDay(selected), endOfDay(selected)), data = _a.data, isLoading = _a.isLoading, isError = _a.isError, error = _a.error, refetch = _a.refetch;
    useEffect(function () {
        if (error) {
            console.error(error);
        }
    }, [error]);
    var ui = useMemo(function () {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p;
        var cfg = (_b = (_a = data === null || data === void 0 ? void 0 : data.agenda) === null || _a === void 0 ? void 0 : _a.uiConfig) !== null && _b !== void 0 ? _b : undefined;
        var requestedLang = ((cfg === null || cfg === void 0 ? void 0 : cfg.defaultLanguageCode) ||
            ((_c = navigator.language) === null || _c === void 0 ? void 0 : _c.split('-')[0]) ||
            "en").toLowerCase();
        // base defaults
        var baseLocale = {
            Title: "Weekly Planner",
            Participants: "Participants",
            "Seats Remaining": "Seats Remaining",
            Close: "Close",
            "Book now": "Book now",
            Reservation: "Reservation",
            Reservations: "Reservations",
            "No reservations yet": "No reservations yet",
            "Cancel": "Cancel",
            "This event has already started": "This event has already started",
            "This event is fully booked": "This event is fully booked"
        };
        var locMap = (_d = cfg === null || cfg === void 0 ? void 0 : cfg.customLocalizations) !== null && _d !== void 0 ? _d : {};
        var locRequested = parseLocaleString(locMap[requestedLang]);
        var locEn = requestedLang === "en" ? {} : parseLocaleString(locMap["en"]);
        // merge priority: requested → en → base
        var locale = __assign(__assign(__assign({}, baseLocale), locEn), locRequested);
        return {
            defaultLanguageCode: requestedLang,
            logo: { mediaURI: (_f = (_e = cfg === null || cfg === void 0 ? void 0 : cfg.logo) === null || _e === void 0 ? void 0 : _e.mediaURI) !== null && _f !== void 0 ? _f : null },
            font: {
                mediaURI: (_j = (_h = (_g = cfg === null || cfg === void 0 ? void 0 : cfg.font) === null || _g === void 0 ? void 0 : _g.font) === null || _h === void 0 ? void 0 : _h.mediaURI) !== null && _j !== void 0 ? _j : "",
                family: (_l = (_k = cfg === null || cfg === void 0 ? void 0 : cfg.font) === null || _k === void 0 ? void 0 : _k.family) !== null && _l !== void 0 ? _l : "inherit",
            },
            css: {
                primary: (_m = cfg === null || cfg === void 0 ? void 0 : cfg.primaryColor) !== null && _m !== void 0 ? _m : "",
                onPrimary: "#ffffff",
                panelBackground: (_o = cfg === null || cfg === void 0 ? void 0 : cfg.backgroundColor) !== null && _o !== void 0 ? _o : "",
            },
            customCSS: (_p = cfg === null || cfg === void 0 ? void 0 : cfg.customCSS) !== null && _p !== void 0 ? _p : "",
            locale: locale,
        };
    }, [data === null || data === void 0 ? void 0 : data.agenda]);
    var events = useMemo(function () {
        if (!(data === null || data === void 0 ? void 0 : data.items))
            return [];
        var lang = ui.defaultLanguageCode;
        return data.items.map(function (item) {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q;
            var service = item.service;
            var serviceId = (_a = service.id) !== null && _a !== void 0 ? _a : '';
            // const serviceName = service.name ?? 'Service';
            // Use localized name if available, fallback to default name
            var serviceName = (_f = (_e = (_c = (_b = service.nameLocalized) === null || _b === void 0 ? void 0 : _b[lang]) !== null && _c !== void 0 ? _c : (_d = service.nameLocalized) === null || _d === void 0 ? void 0 : _d['en']) !== null && _e !== void 0 ? _e : service.name) !== null && _f !== void 0 ? _f : 'Service';
            var description = (_h = (_g = service.shortDescriptionLocalized) === null || _g === void 0 ? void 0 : _g[lang]) !== null && _h !== void 0 ? _h : (_j = service.descriptionLocalized) === null || _j === void 0 ? void 0 : _j[lang];
            var start = new Date(item.startTime);
            var end = new Date(item.endTime);
            var totalCapacity = (_k = item.totalCapacity) !== null && _k !== void 0 ? _k : 0;
            var bookedCapacity = (_l = item.bookedCapacity) !== null && _l !== void 0 ? _l : 0;
            var remainingSeats = Math.max(totalCapacity - bookedCapacity, 0);
            var colorPick = (_p = (_o = (_m = service === null || service === void 0 ? void 0 : service.color) === null || _m === void 0 ? void 0 : _m.background) !== null && _o !== void 0 ? _o : CommonThemeColors.sys.success.background) !== null && _p !== void 0 ? _p : "#9e9e9e";
            var rows = ((_q = service.availabilityBindings) !== null && _q !== void 0 ? _q : []).map(function (binding) {
                var _a, _b, _c, _d, _e, _f;
                return ({
                    availabilityId: (_b = (_a = binding.availabilityIds) === null || _a === void 0 ? void 0 : _a[0]) !== null && _b !== void 0 ? _b : '',
                    availabilityName: (_c = binding.availabilityName) !== null && _c !== void 0 ? _c : '',
                    resourceId: (_d = binding.resourceId) !== null && _d !== void 0 ? _d : '',
                    serviceName: serviceName,
                    serviceImage: (_f = (_e = service.media) === null || _e === void 0 ? void 0 : _e.mediaURI) !== null && _f !== void 0 ? _f : undefined,
                    start: start,
                    end: end,
                    capacity: totalCapacity,
                    color: colorPick,
                });
            });
            return {
                id: serviceId,
                start: start,
                end: end,
                title: serviceName,
                description: description,
                color: colorPick,
                background: colorPick,
                resource: serviceId,
                event: {
                    service: service,
                    rows: rows,
                    totalCapacity: totalCapacity,
                    reservationCount: bookedCapacity,
                    remainingSeats: remainingSeats,
                    earliestStart: start,
                    latestEnd: end,
                    color: colorPick
                },
            };
        });
    }, [data === null || data === void 0 ? void 0 : data.items]);
    return {
        isLoading: isLoading,
        isError: isError,
        events: events,
        ui: ui,
        agenda: data === null || data === void 0 ? void 0 : data.agenda,
        refetch: refetch
    };
};
